<template>
  <BDLayout>
    <template #topbar>
      <div class="d-flex align-items-center gap-2">
        <BDCountrySelector />
        <div class="d-flex align-items-center report-actions">
          <button class="btn btn-sm btn-primary" @click="exportReport" :disabled="loading || reportLoading">
            <span v-if="!reportLoading">Generate Report</span>
            <span v-else>
              <i class="fas fa-file-export fa-fw fa-spin me-1"></i>
              Preparing...
            </span>
          </button>
          <div v-if="reportLoading" class="report-anim ms-2">
            <div class="doc-icon" aria-hidden></div>
            <div class="dots">Preparing report<span class="ellipsis">...</span></div>
          </div>
        </div>
      </div>
    </template>
    <div class="transaction">
      <section id="bd-analytics">
        <div class="container" :class="{ 'animate-values': animateValues }">
          <SkeletonLoader v-if="loading && !filtering" type="stats" :count="4" />
          <div v-else>
          <!-- Filtering overlay when user changes country (animated spinner) -->
          <div v-if="filtering" class="filtering-overlay">
            <div class="filtering-content">
              <div class="spinner" aria-hidden></div>
              <div class="filter-text">Filtering by country<span class="ellipsis">...</span></div>
            </div>
          </div>
       
          <!--<p class="text-muted mb-3">Module 6 – Analytics & Executive Dashboard</p>

           Top KPI cards -->
          <!-- Sector Cards Loop -->
          <div class="warehouse-inventory-card-wrapper mb-3">
            <template v-for="sector in sectorPerformance" :key="sector.sector">
              <div class="warehouse-inventory-card">
                <p>{{ sector.sector || 'Unknown Sector' }}</p>
                <strong>{{ sector.totalOpportunities != null ? sector.totalOpportunities : 0 }}</strong>
              </div>
            </template>
          </div>
          <!-- Existing KPI cards below (optional: keep or move as needed) -->
          <div class="warehouse-inventory-card-wrapper mb-3">
            <div class="warehouse-inventory-card">
              <p>Total Opportunities</p>
              <strong>{{ summary.totalOpportunities != null ? summary.totalOpportunities : 0 }}</strong>
            </div>
            <div class="warehouse-inventory-card">
              <p>Pipeline Value</p>
              <strong>{{ formatCurrency(summary.pipelineValue) }}</strong>
            </div>
            <div class="warehouse-inventory-card">
              <p>Active Tenders</p>
              <strong>{{ summary.activeTenders != null ? summary.activeTenders : 0 }}</strong>
            </div>
            <div class="warehouse-inventory-card">
              <p>Submitted This Month</p>
              <strong>{{ summary.submittedThisMonth != null ? summary.submittedThisMonth : 0 }}</strong>
            </div>
            <div class="warehouse-inventory-card">
              <p>Win Rate</p>
              <strong class="text-success">{{ summary.winRate != null ? summary.winRate : 0 }}%</strong>
            </div>
            <div class="warehouse-inventory-card">
              <p>Loss Rate</p>
              <strong class="text-danger">{{ summary.lostRate != null ? summary.lostRate : 0 }}</strong>
            </div>
            <div class="warehouse-inventory-card">
              <p>Avg Bid Cycle Time</p>
              <strong>{{ summary.avgBidCycleTime != null ? summary.avgBidCycleTime : '-' }} days</strong>
            </div>
            <div class="warehouse-inventory-card">
              <p>Avg Margin</p>
              <strong>{{ summary.avgMargin != null ? summary.avgMargin : 0 }}%</strong>
            </div>
          </div>

          <!-- Second row KPI cards -->
          <div class="warehouse-inventory-card-wrapper mb-4">
            <div class="warehouse-inventory-card">
              <p>Bid Prep Cost (YTD)</p>
              <strong>{{ formatCurrency(summary.bidPreparationCost) }}</strong>
            </div>
            <div class="warehouse-inventory-card">
              <p>Revenue Forecast</p>
              <strong>{{ formatCurrency(summary.revenueForecast) }}</strong>
            </div>
            <div class="warehouse-inventory-card">
              <p>Compliance Health</p>
              <strong :class="complianceClass">{{ complianceLabel }}</strong>
            </div>
          </div>

          <!-- Opportunities vs Contracts trends (comparison) -->
          <div class="row mb-3">
            <div class="col-12 mb-3">
              <div class="card h-100">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="fw-bold mb-0">Opportunities vs Contracts Awarded</h6>
                    <small class="text-muted">Yearly comparison</small>
                  </div>
                  <BDLineChart
                    v-if="opportunitiesContractsOverTime.length"
                    :data="opportunitiesContractsOverTime"
                    categoryKey="period"
                    valueKey="opportunities"
                    :series="opportunitiesContractsSeries"
                    valueLabel="Count"
                  />
                  <p v-else class="text-muted mb-0">No trend data available.</p>
                </div>
              </div>
            </div>
          </div>

          <div class="row">
            <!-- Client win ratio -->
            <div class="col-md-6 mb-3">
              <div class="card h-100">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="fw-bold mb-0">Client Win Ratio</h6>
                    <small class="text-muted">Top clients by win %</small>
                  </div>
                  <BDBarChart
                    v-if="clientWinRatio.length"
                    :data="clientWinRatio"
                    categoryKey="client"
                    valueKey="winRate"
                    valueLabel="Win %"
                  />
                  <p v-else class="text-muted mb-0">No client data available.</p>
                </div>
              </div>
            </div>

            <!-- Sector performance -->
            <div class="col-md-6 mb-3">
              <div class="card h-100">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="fw-bold mb-0">Sector Performance</h6>
                    <small class="text-muted">Win/Loss by sector</small>
                  </div>
                  <BDBarChart
                    v-if="sectorPerformanceStackedData.length"
                    :data="sectorPerformanceStackedData"
                    categoryKey="sector"
                    :series="sectorSeries"
                    valueLabel="Opportunities"
                  />
                  <p v-else class="text-muted mb-0">No sector data available.</p>
                </div>
              </div>
            </div>
          </div>

          <!-- Tender type & country performance -->
          <div class="row mt-3">
            <!-- Tender type performance -->
            <div class="col-md-6 mb-3">
              <div class="card h-100">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="fw-bold mb-0">Tenders by Tender Type</h6>
                    <small class="text-muted">Opportunities, wins & pipeline</small>
                  </div>
                  <BDPieChart
                    v-if="tenderTypePerformance.length"
                    :data="tenderTypePerformance"
                    categoryKey="tenderType"
                    valueKey="pipelineValue"
                    valueLabel="Pipeline"
                  />
                  <p v-else class="text-muted mb-0">No tender type data available.</p>
                </div>
              </div>
            </div>

            <!-- Department performance -->
            <div class="col-md-6 mb-3">
              <div class="card h-100">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="fw-bold mb-0">Tenders by Department</h6>
                    <small class="text-muted">Opportunities & pipeline by department</small>
                  </div>
                  <BDPieChart
                    v-if="departmentPerformance.length"
                    :data="departmentPerformance"
                    categoryKey="department"
                    valueKey="pipelineValue"
                    valueLabel="Pipeline"
                  />
                  <p v-else class="text-muted mb-0">No department data available.</p>
                </div>
              </div>
            </div>
          </div>
          <!-- Africa map visualization with horizontal bars -->
          <div class="row mt-3">
            <div class="col-12 mb-3">
              <div class="card h-100">
                <div class="card-body">
                  <div class="d-flex justify-content-between align-items-center mb-2">
                    <h6 class="fw-bold mb-0">Africa Map – Tenders by Country</h6>
                  </div>
                  <div class="row align-items-stretch">
                    <div class="col-md-7 mb-3 mb-md-0">
                      <AfricaMap :data="countryPerformance" metric="pipelineValue" />
                    </div>
                    <div class="col-md-5">
                      <BDBarChart
                        v-if="countryPerformance.length"
                        :data="countryPerformance"
                        categoryKey="country"
                        valueKey="pipelineValue"
                        valueLabel="Pipeline"
                        orientation="bar"
                      />
                      <p v-else class="text-muted mb-0">No country data available.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
      </section>
    </div>
  </BDLayout>
</template>

<script>
import { reactive, ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'
import BDLayout from '@/components/BDLayout.vue'
import BDCountrySelector from '@/components/BDCountrySelector.vue'
import AfricaMap from '@/components/AfricaMap.vue'
import BDBarChart from '@/components/BDBarChart.vue'
import BDPieChart from '@/components/BDPieChart.vue'
import BDLineChart from '@/components/BDLineChart.vue'
import SkeletonLoader from '@/components/SkeletonLoader.vue'
import { bidApi } from '@/api/bid'

export default {
  name: 'BDDashboard',
  components: { BDLayout, BDCountrySelector, AfricaMap, BDBarChart, BDPieChart, BDLineChart, SkeletonLoader },
  setup() {
    const loading = ref(false)
    const filtering = ref(false)
    const summary = reactive({
      totalOpportunities: 0,
      pipelineValue: 0,
      activeTenders: 0,
      submittedThisMonth: 0,
      avgBidCycleTime: 0,
      winRate: 0,
      lostRate: 0,
      avgMargin: 0,
      bidPreparationCost: 0,
      revenueForecast: 0,
      complianceHealthStatus: null,
    })

    const clientWinRatio = ref([])
    const sectorPerformance = ref([])
    const tenderTypePerformance = ref([])
    const countryPerformance = ref([])
    const departmentPerformance = ref([])
    const opportunitiesOverTime = ref([])
    const contractsAwardedOverTime = ref([])

    const sectorSeries = [
      { key: 'wins', label: 'Wins' },
      { key: 'losses', label: 'Losses' },
    ]
    const opportunitiesContractsSeries = [
      { key: 'opportunities', label: 'Opportunities' },
      { key: 'contracts', label: 'Contracts Awarded' },
    ]

    const loadSummary = async (country) => {
      loading.value = true
      try {
        const params = country ? { country } : undefined
        const data = await bidApi.getAnalyticsSummary(params)
        const payload = data.data || data
        Object.assign(summary, {
          ...summary,
          ...payload,
        })
        if (Array.isArray(payload.clientWinRatio)) clientWinRatio.value = payload.clientWinRatio
        if (Array.isArray(payload.sectorPerformance)) sectorPerformance.value = payload.sectorPerformance
        if (Array.isArray(payload.tenderTypePerformance)) tenderTypePerformance.value = payload.tenderTypePerformance
        if (Array.isArray(payload.countryPerformance)) countryPerformance.value = payload.countryPerformance
        if (Array.isArray(payload.departmentPerformance)) departmentPerformance.value = payload.departmentPerformance
        if (Array.isArray(payload.opportunitiesOverTime)) opportunitiesOverTime.value = payload.opportunitiesOverTime
        if (Array.isArray(payload.contractsAwardedOverTime)) contractsAwardedOverTime.value = payload.contractsAwardedOverTime
      } catch (err) {
        console.error('Failed to load BD analytics summary', err)
      } finally {
        loading.value = false
        // trigger a subtle animation on KPI values
        try {
          animateValues.value = false
          // allow DOM to settle then animate
          nextTick(() => {
            animateValues.value = true
            setTimeout(() => { animateValues.value = false }, 800)
          })
        } catch (e) { /* ignore */ }
      }
    }

    const complianceLabel = computed(() => {
      const h = summary.complianceHealthStatus
      if (!h) return 'N/A'
      if (h === 'GOOD' || h === 'OK' || h === 'HEALTHY') return 'Healthy'
      if (h === 'WARNING') return 'Warning'
      if (h === 'CRITICAL' || h === 'EXPIRED' || h === 'UNHEALTHY') return 'At Risk'
      return h
    })

    const complianceClass = computed(() => {
      const h = summary.complianceHealthStatus
      switch (h) {
        case 'GOOD':
        case 'OK':
        case 'HEALTHY':
          return 'text-success'
        case 'WARNING':
          return 'text-warning'
        case 'CRITICAL':
        case 'EXPIRED':
        case 'UNHEALTHY':
          return 'text-danger'
        default:
          return 'text-muted'
      }
    })

    const formatCurrency = (val) => {
      if (val === null || val === undefined) return '-'
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        maximumFractionDigits: 0,
      }).format(val)
    }

    const sectorPerformanceStackedData = computed(() => {
      if (!Array.isArray(sectorPerformance.value)) return []
      return sectorPerformance.value.map((row) => {
        const total = Number(row.totalOpportunities ?? 0)
        const wins = Number(row.wins ?? 0)
        const losses = total - wins
        const safeWins = isNaN(wins) ? 0 : wins
        const safeLosses = isNaN(losses) || losses < 0 ? 0 : losses
        return {
          sector: row.sector || 'Unknown',
          wins: safeWins,
          losses: safeLosses,
        }
      })
    })

    const opportunitiesContractsOverTime = computed(() => {
      const map = {}

      if (Array.isArray(opportunitiesOverTime.value)) {
        opportunitiesOverTime.value.forEach((row) => {
          const period = row.period || 'Unknown'
          if (!map[period]) {
            map[period] = { period, opportunities: 0, contracts: 0 }
          }
          const v = Number(row.count ?? 0)
          map[period].opportunities += isNaN(v) ? 0 : v
        })
      }

      if (Array.isArray(contractsAwardedOverTime.value)) {
        contractsAwardedOverTime.value.forEach((row) => {
          const period = row.period || 'Unknown'
          if (!map[period]) {
            map[period] = { period, opportunities: 0, contracts: 0 }
          }
          const v = Number(row.count ?? 0)
          map[period].contracts += isNaN(v) ? 0 : v
        })
      }

      return Object.keys(map)
        .sort()
        .map((k) => map[k])
    })

    const selectedCountry = ref(null)
    const reportLoading = ref(false)
    const animateValues = ref(false)

    const onBdCountryChanged = async (e) => {
      const c = e && e.detail ? e.detail.country : null
      selectedCountry.value = c || null
      // prefer country name for matching against Client.country in the DB
      const countryVal = c ? (c.name || c.code || c.id || c) : null
      try {
        filtering.value = true
        await loadSummary(countryVal)
      } finally {
        filtering.value = false
      }
    }

    const exportReport = async () => {
      const c = selectedCountry.value
      const countryVal = c ? (c.name || c.code || c.id || c) : null
      try {
        reportLoading.value = true
        const params = countryVal ? { country: countryVal } : undefined
        const { blob, disposition } = await bidApi.generateAnalyticsReport(params)
        const url = window.URL.createObjectURL(blob)
        let filename = 'bd-report.csv'
        if (disposition) {
          const match = /filename\*=UTF-8''(.+)$/.exec(disposition) || /filename="?([^";]+)"?/.exec(disposition)
          if (match && match[1]) filename = decodeURIComponent(match[1])
        }
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      } catch (err) {
        console.error('Failed to export BD report', err)
        try { alert((err && err.message) || 'Failed to generate report') } catch (e) { /* ignore */ }
      } finally {
        reportLoading.value = false
      }
    }

    onMounted(() => {
      // initialize from localStorage if present
      try {
        const _bd = JSON.parse(localStorage.getItem('bd_selected_country') || 'null')
        if (_bd) {
          selectedCountry.value = _bd
        }
      } catch (e) { /* ignore */ }
      const initCountry = selectedCountry.value ? (selectedCountry.value.id || selectedCountry.value.code || selectedCountry.value.name) : null
      loadSummary(initCountry)
      window.addEventListener('bd-country-changed', onBdCountryChanged)
    })

    onBeforeUnmount(() => {
      try { window.removeEventListener('bd-country-changed', onBdCountryChanged) } catch (e) { /* ignore */ }
    })

    return {
      loading,
      filtering,
      animateValues,
      exportReport,
      reportLoading,
      summary,
      clientWinRatio,
      sectorPerformance,
      sectorSeries,
      sectorPerformanceStackedData,
      tenderTypePerformance,
      countryPerformance,
      departmentPerformance,
      opportunitiesOverTime,
      contractsAwardedOverTime,
      opportunitiesContractsSeries,
      opportunitiesContractsOverTime,
      complianceLabel,
      complianceClass,
      formatCurrency,
    }
  },
}
</script>

<style scoped>
/* Align KPI label on the left and value on the right */
.warehouse-inventory-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.warehouse-inventory-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 80px;
}

.warehouse-inventory-card p {
}

/* Report preparing animation */
.report-anim {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #444;
  font-size: 0.9rem;
}
.report-anim .doc-icon {
  width: 22px;
  height: 28px;
  background: linear-gradient(180deg,#fff,#f0f0f0);
  border: 1px solid #d1d5db;
  border-radius: 3px;
  position: relative;
  box-shadow: 0 1px 2px rgba(0,0,0,0.06);
  animation: float 1.2s ease-in-out infinite;
}
.report-anim .doc-icon:after {
  content: '';
  position: absolute;
  right: 3px;
  top: 4px;
  width: 8px;
  height: 8px;
  border-radius: 1px;
  background: #3b82f6;
}
.report-anim .dots .ellipsis {
  display: inline-block;
  width: 30px;
  text-align: left;
}
.report-anim .ellipsis::after {
  content: '...';
  display: inline-block;
  animation: ellipsis 1s steps(4,end) infinite;
}

@keyframes float {
  0% { transform: translateY(0) }
  50% { transform: translateY(-4px) }
  100% { transform: translateY(0) }
}

@keyframes ellipsis {
  0% { content: ''; }
  33% { content: '.'; }
  66% { content: '..'; }
  100% { content: '...'; }
}

/* Filtering overlay styles */
.filtering-overlay {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: rgba(255,255,255,0.65);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 50;
  transition: opacity 240ms ease;
}
.filtering-content {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 13px 18px;
  background: rgba(255,255,255,0.9);
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
}
.filter-text {
  color: #374151;
  font-weight: 500;
}
.spinner {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 4px solid #e6eefc;
  border-top-color: #2563eb;
  animation: spin 0.9s linear infinite;
}
.ellipsis::after {
  content: '.';
  animation: ellipsis 1s steps(4,end) infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* KPI value pop animation */
.animate-values .warehouse-inventory-card strong {
  display: inline-block;
  transform-origin: center bottom;
  animation: pop 700ms cubic-bezier(0.2,0.9,0.2,1) 1;
}

@keyframes pop {
  0% { transform: translateY(6px) scale(0.98); opacity: 0.0 }
  40% { transform: translateY(-2px) scale(1.03); opacity: 1 }
  100% { transform: translateY(0) scale(1); opacity: 1 }
}
</style>
